import { z } from 'zod'

import { CAMPAIGN_MEMO_MAX_LENGTH } from '@/common/constants'

import { APIError } from '../api/error'

// Campaign.targetSns
export const CampaignTargetSnsType = {
  TWITTER: 'twitter',
  INSTAGRAM: 'instagram',
  TIKTOK: 'tiktok'
} as const
export type CampaignTargetSnsType = (typeof CampaignTargetSnsType)[keyof typeof CampaignTargetSnsType]
export const CampaignTargetSnsTypeSchema = z.nativeEnum(CampaignTargetSnsType)

// Campaign.type
export const CampaignType = {
  KEYWORD: 'keyword',
  OLD_WEB_INSTANTWIN: 'old_web_instantwin',
  INSTANTWIN: 'instantwin'
} as const
export type CampaignType = (typeof CampaignType)[keyof typeof CampaignType]
export const CampaignTypeSchema = z.nativeEnum(CampaignType)

// キャンペーン開催状況ステータス.
export const CampaignStatusType = {
  BEFORE_EVENT: 'beforeEvent',
  DURING_COLLECTION: 'duringCollection',
  COLLECTION_COMPLETED: 'collectionCompleted'
} as const
export type CampaignStatusType = (typeof CampaignStatusType)[keyof typeof CampaignStatusType]
export const CampaignStatusTypeSchema = z.nativeEnum(CampaignStatusType)

// entryActionType.
const TwitterEntryActionType = {
  RETWEET: 'retweet',
  QUOTE_TWEET: 'quote_tweet',
  RETWEET_AND_QUOTE_TWEET: 'retweet_and_quote_tweet',
  TWEET: 'tweet',
  TWEET_MEDIA: 'tweet_media',
  MENTION: 'mention'
} as const
type TwitterEntryActionType = (typeof TwitterEntryActionType)[keyof typeof TwitterEntryActionType]

const InstagramEntryActionType = {
  COMMENT: 'comment',
  MENTION: 'mention'
} as const
type InstagramEntryActionType = (typeof InstagramEntryActionType)[keyof typeof InstagramEntryActionType]

const TiktokEntryActionType = {
  COMMENT: 'comment'
} as const
type TiktokEntryActionType = (typeof TiktokEntryActionType)[keyof typeof TiktokEntryActionType]

export type CampaignEntryActionType = TwitterEntryActionType | InstagramEntryActionType | TiktokEntryActionType
export const CampaignEntryActionTypeSchema = z.union([
  z.nativeEnum(TwitterEntryActionType),
  z.nativeEnum(InstagramEntryActionType),
  z.nativeEnum(TiktokEntryActionType)
])

export type WebInstantwinLayoutType = 'basic' | 'custom'

export type WebInstantwinBasicLayout = {
  page_title: string
  page_main_image_url?: string | null
  page_main_image_origin_name?: string | null
  page_text?: string | null
  page_rules?: string | null
  is_browser_guide_enabled?: boolean
}

export type WebInstantwinCustomLayoutSectionMedia = {
  section_id?: number
  mode: 'media'
  name?: string
  step_order: number
  pc_media_name?: string | null
  pc_media_url?: string | null
  pc_media_size: number
  sp_media_name?: string | null
  sp_media_url?: string | null
  url?: string | null
  background_color?: string | null
}

export type WebInstantwinCustomLayoutSectionRule = {
  section_id?: number
  mode: 'rule'
  name?: string
  step_order: number
  page_rules: string
  background_color?: string | null
  apply_button_background_color?: string | null
  apply_button_text_color?: string | null
  apply_text_color?: string | null
  apply_warning_text_color?: string | null
  apply_error_text_color?: string | null
  rule_button_background_color?: string | null
  rule_button_text_color?: string | null
}

export type WebInstantwinCustomLayout = {
  ogp_title: string
  ogp_main_image_url: string
  ogp_main_image_origin_name: string
  logo_image_url?: string | null
  logo_image_origin_name?: string | null
  logo_url?: string | null
  logo_background_color?: string | null
  logo_is_enabled: boolean
  logo_header_pc_height?: number | null
  logo_header_sp_height?: number | null
  body_background_color?: string | null
  footer_title?: string | null
  footer_background_color?: string | null
  footer_text?: string | null
  sections: Array<WebInstantwinCustomLayoutSectionMedia | WebInstantwinCustomLayoutSectionRule>
}

export type WebInstantwinTwSettingPayload = {
  layout_type: WebInstantwinLayoutType
  basic_layout?: WebInstantwinBasicLayout
  custom_layout?: WebInstantwinCustomLayout
}

export type WebInstantwinTwSettingRequest = WebInstantwinTwSettingPayload & {
  entry_action_type: string
  search_keyword: string[]
  is_include_reply?: boolean
  exclude_hunter_account?: boolean
  exclude_account_create_date_within_period?: boolean
  exclude_default_profile_image?: boolean
  exclude_no_description?: boolean
  exclude_unfollowed?: boolean
  min_followers?: number
  min_tweets?: number
  exclude_description_keyword?: string[]
}

/**
 * 共通定義
 */
export type Campaign = {
  id: number
  type: string
  status: number
  target_sns: string
  title: string
  start_datetime: Date
  end_datetime: Date
  account?: {
    id: string
    name: string
    picture_url: string
  }
  tw_setting?: {
    entry_action_type: TwitterEntryActionType
    search_keyword: string[]
    or_keyword: string[]
    positive_keyword: string[]
    negative_keyword: string[]
    is_include_mention: boolean
    is_include_reply: boolean
    layout_type?: WebInstantwinLayoutType
    basic_layout?: WebInstantwinBasicLayout | null
    custom_layout?: WebInstantwinCustomLayout | null
  }
  in_setting?: {
    entry_action_type: InstagramEntryActionType
    search_keyword: string[]
  }
  in_instantwin_setting?: {
    entry_action_type: InstagramEntryActionType
    search_keyword: string[]
    is_daily_lottery?: boolean
    exclude_multi_winner?: boolean
    exclude_other_campaign_winner?: boolean
  }
  tt_setting?: {
    entry_action_type: TiktokEntryActionType
    search_keyword: string[]
  }
}

/**
 * GetCampaigns
 */
export type GetCampaignQuery = {
  group_id: number
  sort?: string
  limit: number
  offset: number
}

export type GetCampaignData = {
  rows: Campaign[]
  count: number
}

export type GetCampaignsResponse = {
  data?: GetCampaignData
  error?: APIError
}

/**
 * GetCampaignById
 */
export type GetCampaignByIdParams = {
  campaign_id: number
}

export type GetCampaignByIdData = {
  id: number
  group_id: number
  title: string
  status: number
  start_datetime: Date
  end_datetime: Date
  type: string
  target_sns: string
  account: {
    id: string | number
    name: string
    picture_url: string
  } | null
  tw_setting: {
    entry_action_type: string
    search_keyword: string[]
    or_keyword?: string[] | null
    is_include_mention?: boolean
    is_include_reply?: boolean
    positive_keyword?: string[]
    negative_keyword?: string[]
    require_follow?: boolean
    exclude_hunter_account?: boolean
    exclude_account_create_date_within_period?: boolean
    exclude_default_profile_image?: boolean
    exclude_no_description?: boolean
    exclude_unfollowed?: boolean
    min_followers?: number
    daily_send_limit_count?: number
    min_tweets?: number
    exclude_description_keyword?: string[]
    page_main_image_url?: string
    page_main_image_origin_name?: string
    page_title?: string
    page_text?: string
    page_rules?: string
    page_hash?: string
    layout_type?: WebInstantwinLayoutType
    basic_layout?: WebInstantwinBasicLayout | null
    custom_layout?: WebInstantwinCustomLayout | null
  } | null
  in_setting: {
    entry_action_type: string
    search_keyword: string[]
  } | null
  in_instantwin_setting: {
    entry_action_type: string
    search_keyword: string[]
    is_daily_lottery?: boolean
    exclude_multi_winner?: boolean
    exclude_other_campaign_winner?: boolean
  } | null
  tt_setting: {
    entry_action_type: string
    search_keyword: string[]
  } | null
}

export type GetCampaignByIdResponse = {
  data?: GetCampaignByIdData
  error?: APIError
}

/**
 * GetCampaignStatistics
 */
export type GetCampaignStatisticsParams = {
  campaign_id: number
}

export type GetCampaignStatisticsData = {
  applicant_count: number
  winner_count: number
  max_winner_count: number
  responder_count: number
  remain_winner_count: number
  entry_count?: number
  lottery_count?: number
}

/**
 * GetValidations
 */

export type GetValidationQuery = {
  group_id: number
  start_datetime: string
  end_datetime: string
  type: 'keyword' | 'instantwin'
  exclude_campaign_id?: number
}

export type GetValidationResponse = {
  data?: boolean
  error?: APIError
}

/**
 * DeleteCampaigns
 */
export type DeleteCampaignsParams = {
  campaign_id: number
}

export type DeleteCampaignResponse = {
  data?: boolean
  error?: APIError
}

/**
 * PostCampaigns
 */
const TwSettingSchema = z
  .object({
    entry_action_type: z.nativeEnum(TwitterEntryActionType),
    search_keyword: z.array(z.string()),
    or_keyword: z.array(z.string()).optional(),
    is_include_mention: z.boolean().optional(),
    is_include_reply: z.boolean().optional(),
    positive_keyword: z.array(z.string()).optional(),
    negative_keyword: z.array(z.string()).optional(),
    require_follow: z.boolean().optional(),
    exclude_hunter_account: z.boolean().optional(),
    exclude_account_create_date_within_period: z.boolean().optional(),
    exclude_default_profile_image: z.boolean().optional(),
    exclude_no_description: z.boolean().optional(),
    exclude_unfollowed: z.boolean().optional(),
    min_followers: z.number().optional(),
    daily_send_limit_count: z.number().optional(),
    min_tweets: z.number().optional(),
    exclude_description_keyword: z.array(z.string()).optional(),
    campaign_account_screen_name: z
      .string()
      .regex(/^[a-zA-Z0-9_]+$/)
      .refine(val => isNaN(Number(val)))
      .optional(),
    page_title: z.string().max(50).optional(),
    page_main_image_url: z.string().url().optional(),
    page_main_image_origin_name: z.string().nullable().optional(),
    page_text: z.string().optional(),
    page_rules: z.string().optional(),
    layout_type: z.enum(['basic', 'custom']).optional(),
    basic_layout: z
      .object({
        page_title: z.string().max(50).min(1),
        page_main_image_url: z.string().url().optional(),
        page_main_image_origin_name: z.string().nullable().optional(),
        page_text: z.string().optional(),
        page_rules: z.string().optional(),
        is_browser_guide_enabled: z.boolean().optional()
      })
      .optional(),
    custom_layout: z
      .object({
        ogp_title: z.string().max(50).min(1),
        ogp_main_image_url: z.string().url(),
        ogp_main_image_origin_name: z.string(),
        logo_image_url: z.string().url().nullable().optional(),
        logo_image_origin_name: z.string().nullable().optional(),
        logo_url: z.string().url().nullable().optional(),
        logo_background_color: z.string().nullable().optional(),
        logo_is_enabled: z.boolean(),
        footer_title: z.string().nullable().optional(),
        footer_background_color: z.string().nullable().optional(),
        sections: z.array(
          z.union([
            z.object({
              section_id: z.number().optional(),
              mode: z.literal('media'),
              name: z.string().optional(),
              step_order: z.number(),
              pc_media_name: z.string().nullable().optional(),
              pc_media_url: z.string().nullable().optional(),
              pc_media_size: z.number(),
              sp_media_name: z.string().nullable().optional(),
              sp_media_url: z.string().nullable().optional(),
              url: z.string().nullable().optional(),
              background_color: z.string().nullable().optional()
            }),
            z.object({
              section_id: z.number().optional(),
              mode: z.literal('rule'),
              name: z.string().optional(),
              step_order: z.number(),
              page_rules: z.string().min(1),
              background_color: z.string().nullable().optional()
            })
          ])
        )
      })
      .optional()
  })
  .superRefine((value, ctx) => {
    const hasLayoutPayload = Boolean(value.layout_type || value.basic_layout || value.custom_layout)

    if (!value.layout_type) {
      if (hasLayoutPayload) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['layout_type'],
          message: 'layout_type は必須です。'
        })
      }
      return
    }

    if (value.layout_type === 'basic') {
      if (!value.basic_layout) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['basic_layout'],
          message: 'basic_layout は必須です。'
        })
        return
      }

      if (!value.basic_layout.page_title.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['basic_layout', 'page_title'],
          message: 'page_title は必須です。'
        })
      }

      return
    }

    if (value.layout_type !== 'custom') {
      return
    }

    if (!value.custom_layout) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['custom_layout'],
        message: 'custom_layout は必須です。'
      })
      return
    }

    if (!value.custom_layout.ogp_title.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['custom_layout', 'ogp_title'],
        message: 'ogp_title は必須です。'
      })
    }

    const sections = value.custom_layout.sections
    if (!sections.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['custom_layout', 'sections'],
        message: 'sections は必須です。'
      })
      return
    }

    const stepOrderSet = new Set<number>()
    let ruleCount = 0
    let mediaCount = 0

    sections.forEach((section, index) => {
      if (stepOrderSet.has(section.step_order)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['custom_layout', 'sections', index, 'step_order'],
          message: 'step_order が重複しています。'
        })
      } else {
        stepOrderSet.add(section.step_order)
      }

      if (section.mode === 'rule') {
        ruleCount += 1
      }

      if (section.mode === 'media') {
        mediaCount += 1
        if (section.url && !/^https?:\/\//i.test(section.url)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['custom_layout', 'sections', index, 'url'],
            message: 'url は http(s) 形式のみ許可されます。'
          })
        }
      }
    })

    if (ruleCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['custom_layout', 'sections'],
        message: 'rule セクションは1件のみです。'
      })
    }

    if (mediaCount === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['custom_layout', 'sections'],
        message: 'media セクションが必要です。'
      })
    }
  })

const InSettingSchema = z.object({
  entry_action_type: z.nativeEnum(InstagramEntryActionType),
  search_keyword: z.array(z.string())
})

const TtSettingSchema = z.object({
  entry_action_type: z.nativeEnum(TiktokEntryActionType),
  search_keyword: z.array(z.string())
})

const InInstantwinSettingSchema = z.object({
  entry_action_type: z.enum(['comment']),
  search_keyword: z.array(z.string()),
  // Instagramインスタントウィン用 抽選ルール
  is_daily_lottery: z.boolean().optional(),
  exclude_multi_winner: z.boolean().optional(),
  exclude_other_campaign_winner: z.boolean().optional()
})

export const CampaignSchema = z.object({
  group_id: z.number(),
  title: z.string().max(50),
  account_id: z.number().or(z.string()),
  start_datetime: z.string(),
  end_datetime: z.string(),
  target_sns: CampaignTargetSnsTypeSchema,
  campaign_type: CampaignTypeSchema,
  tw_setting: TwSettingSchema.optional(),
  in_setting: InSettingSchema.optional(),
  tt_setting: TtSettingSchema.optional(),
  in_instantwin_setting: InInstantwinSettingSchema.optional()
})

export type PostCampaignBody = z.infer<typeof CampaignSchema>

export type PostCampaignData = {
  id: number
}

export type PostCampaignResponse = {
  data?: PostCampaignData
  error?: APIError
}

/**
 * PutCampaigns
 */

export const PutCampaignSchema = CampaignSchema.extend({
  campaign_id: z.number()
}).omit({
  group_id: true,
  account_id: true
})

export type PutCampaignBody = z.infer<typeof PutCampaignSchema>

export type PutCampaignResponse = {
  data?: boolean
  error?: APIError
}

/**
 * GetCampaignMemo
 */
export const GetCampaignMemoParamsSchema = z.object({
  campaign_id: z.number().int().positive()
})

export type GetCampaignMemoParams = z.infer<typeof GetCampaignMemoParamsSchema>

export type GetCampaignMemoData = {
  campaign_title: string
  memo: string | null
}

export type GetCampaignMemoResponse = {
  data?: GetCampaignMemoData
  error?: APIError
}

/**
 * PutCampaignMemo
 */
export const PutCampaignMemoSchema = z.object({
  campaign_id: z.number().int().positive(),
  memo: z.string().max(CAMPAIGN_MEMO_MAX_LENGTH)
})

export type PutCampaignMemoBody = z.infer<typeof PutCampaignMemoSchema>

export type PutCampaignMemoResponse = {
  data?: boolean
  error?: APIError
}

/**
 * CampaignTweetPosts
 */
export const CampaignTweetPostsParamsSchema = z.object({
  campaignId: z.coerce.number().int().positive()
})

export type CampaignTweetPostsParams = z.infer<typeof CampaignTweetPostsParamsSchema>

// GET /campaigns/:campaignId/tweet-posts レスポンスの投稿1件
// サーバの GetCampaignTweetPostsPost に対応
export type GetCampaignTweetPostsPost = {
  postId: string
  screenName: string
  url: string
  deletable: boolean
}

export type GetCampaignTweetPostsData = {
  posts: GetCampaignTweetPostsPost[]
  maxTweetPostUrlCount: number
}

export type GetCampaignTweetPostsResponse = {
  data?: GetCampaignTweetPostsData
  error?: APIError
}

export const PostCampaignTweetPostsBodySchema = z.object({
  urls: z.array(z.string())
})

export type PostCampaignTweetPostsBody = z.infer<typeof PostCampaignTweetPostsBodySchema>
